// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import {
  InstrumentAllowance,
  TransferPreapproval,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/TransferPreapproval';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import Loading from '../../../components/Loading';
import { useParties } from '../../../contexts/PartiesContext';
import { useCreateTransferPreapproval } from '../../../hooks/mutations/registry/transferPreapproval/useCreateTransferPreapproval';
import { useModifyTransferPreapproval } from '../../../hooks/mutations/registry/transferPreapproval/useModifyTransferPreapproval';
import useTransferPreapprovals from '../../../hooks/queries/registry/transferPreapproval/useTransferPreapprovals';
import { useGetAllInstrumentConfigurations } from '../../../hooks/queries/useGetAllInstrumentConfigurations';

// Maximum number of allowed instrument preapprovals
const MAX_INSTRUMENT_PREAPPROVED = 10; // Updated to include 'const'

// Helper: check if all instrument IDs are unique
function isUnique(instrumentAllowances: InstrumentAllowance[]): boolean {
  const ids = instrumentAllowances.map((a) => a.id);
  return new Set(ids).size === ids.length;
}

// Helper: check if number of preapproved instruments is within limit
function maxPreapproved(instrumentAllowances: InstrumentAllowance[]): boolean {
  return instrumentAllowances.length <= MAX_INSTRUMENT_PREAPPROVED;
}

// Main validation function
function isValidInstrumentAllowances(instrumentAllowances: InstrumentAllowance[]): boolean {
  return isUnique(instrumentAllowances) && maxPreapproved(instrumentAllowances);
}

type InstrumentConfig = {
  contract: { contractId: string };
  payload: { defaultIdentifier: { source: string; id: string } };
};

// Helper: extract unique admins from instrument configurations
const extractUniqueAdmins = (configs: InstrumentConfig[]): string[] =>
  configs
    .map((config) => config.payload?.defaultIdentifier?.source || '')
    .filter((admin, idx, arr) => admin && arr.indexOf(admin) === idx);

// Helper: filter and sort instruments by selected admin
const getInstrumentsByAdmin = (
  configs: InstrumentConfig[],
  selectedAdmin: string,
): { instrumentId: string }[] =>
  configs
    .filter(
      (config) => !selectedAdmin || config.payload?.defaultIdentifier?.source === selectedAdmin,
    )
    .map((config) => ({ instrumentId: config.payload?.defaultIdentifier?.id }))
    .filter((inst) => inst.instrumentId)
    .sort((a, b) => a.instrumentId.localeCompare(b.instrumentId));

// Helper: check if two allowance lists are equal (same instruments, ignoring minAmount and order)
const areAllowancesEqual = (
  allowances1: InstrumentAllowance[] | null | undefined,
  allowances2: InstrumentAllowance[] | null | undefined,
): boolean => {
  const a1 = allowances1 ?? [];
  const a2 = allowances2 ?? [];

  // Both empty means "all instruments allowed"
  if (a1.length === 0 && a2.length === 0) return true;
  if (a1.length !== a2.length) return false;

  // Sort by id and compare
  const sorted1 = [...a1].sort((a, b) => a.id.localeCompare(b.id)).map((a) => a.id);
  const sorted2 = [...a2].sort((a, b) => a.id.localeCompare(b.id)).map((a) => a.id);

  return sorted1.every((id, idx) => id === sorted2[idx]);
};

// Helper: check if duplicate preapproval exists
const isDuplicatePreapproval = (
  existingPreapprovals: Array<{
    payload: TransferPreapproval;
    contractId: ContractId<TransferPreapproval>;
  }>,
  instrumentAdmin: string,
  instrumentAllowances: InstrumentAllowance[],
  currentContractId?: ContractId<TransferPreapproval>,
): boolean => {
  return existingPreapprovals.some(
    (p) =>
      p.payload.instrumentAdmin === instrumentAdmin &&
      areAllowancesEqual(p.payload.instrumentAllowances, instrumentAllowances) &&
      (!currentContractId || p.contractId !== currentContractId),
  );
};

// Helper: check if form is valid for submission
const isFormValid = (
  selectedAdmin: string,
  allAllowed: boolean,
  instrumentAllowances: InstrumentAllowance[],
): boolean =>
  !!selectedAdmin &&
  selectedAdmin.trim() !== '' &&
  (allAllowed || isValidInstrumentAllowances(instrumentAllowances));

export type TransferPreapprovalDialogProps = {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'modify';
  modifyContract?: {
    contractId: ContractId<TransferPreapproval>;
    payload: TransferPreapproval;
  };
};

export const TransferPreapprovalDialog: FC<TransferPreapprovalDialogProps> = ({
  open,
  onClose: handleClose,
  mode,
  modifyContract,
}) => {
  // Loading state for async actions
  const [loading, setLoading] = useState(false);
  const { operator, party } = useParties();

  // Mutation hooks
  const createTransferPreapproval = useCreateTransferPreapproval();
  const modifyTransferPreapproval = useModifyTransferPreapproval();

  // Fetch existing preapprovals to check for duplicates
  const existingPreapprovals = useTransferPreapprovals();
  const existingPreapprovalsList = existingPreapprovals.data || [];

  // State variables
  const [selectedAdmin, setSelectedAdmin] = useState<string>('');
  const [instrumentAllowances, setInstrumentAllowances] = useState<InstrumentAllowance[]>([]);
  const [allAllowed, setAllAllowed] = useState<boolean>(true);

  // Check if current form values would create a duplicate
  const hasDuplicate = isDuplicatePreapproval(
    existingPreapprovalsList,
    selectedAdmin,
    allAllowed ? [] : instrumentAllowances,
    modifyContract?.contractId,
  );

  // Reset instrumentAllowances and allAllowed when selectedAdmin changes, only in create mode
  useEffect(() => {
    if (mode === 'create') {
      setInstrumentAllowances([]);
      setAllAllowed(true);
    }
  }, [selectedAdmin, mode]);

  // Initialize state from modifyContract when in modify mode
  useEffect(() => {
    if (open) {
      if (mode === 'modify' && modifyContract) {
        setSelectedAdmin(modifyContract.payload?.instrumentAdmin ?? '');
        const allowances = Array.isArray(modifyContract.payload?.instrumentAllowances)
          ? modifyContract.payload.instrumentAllowances.map((a) => ({ id: a.id ?? '' }))
          : [];
        setInstrumentAllowances(allowances);
        setAllAllowed(!allowances.length);
      } else if (mode === 'create') {
        setSelectedAdmin('');
        setInstrumentAllowances([]);
        setAllAllowed(true);
      }
    }
  }, [mode, modifyContract, open]);

  // Fetch instrument configurations for dropdowns
  const instconfigs = useGetAllInstrumentConfigurations();
  const instrumentConfigurations: InstrumentConfig[] = Array.isArray(
    instconfigs.data?.instrumentConfigurations,
  )
    ? instconfigs.data?.instrumentConfigurations
    : [];

  // Populate allAdmins and allInstruments from instrumentConfigurations
  const allAdmins = extractUniqueAdmins(instrumentConfigurations);
  const allInstruments = getInstrumentsByAdmin(instrumentConfigurations, selectedAdmin);

  // Submit handler
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const instrumentAllowancesUsed = allAllowed ? [] : instrumentAllowances;

      if (mode === 'create') {
        await createTransferPreapproval({
          argument: {
            operator,
            receiver: party,
            instrumentAdmin: selectedAdmin,
            instrumentAllowances: instrumentAllowancesUsed,
          },
        });
      } else if (mode === 'modify') {
        if (!modifyContract) {
          throw new Error('No contract selected for modification.');
        }
        await modifyTransferPreapproval({
          cid: modifyContract.contractId,
          argument: {
            newInstrumentAllowances: instrumentAllowancesUsed,
          },
        });
      }

      // Reset form and close
      setSelectedAdmin('');
      setAllAllowed(true);
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  // Only validate instrument allowances if not allAllowed and dialog is open
  const instrumentAllowancesValid =
    allAllowed || (open && isValidInstrumentAllowances(instrumentAllowances));

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? 'Create Transfer Preapproval' : 'Modify Transfer Preapproval'}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName={mode === 'create' ? 'Create' : 'Modify'}
      isSubmitDisabled={
        loading || !isFormValid(selectedAdmin, allAllowed, instrumentAllowances) || hasDuplicate
      }
      disableDialog={false}
    >
      {loading && <Loading text="Processing..." />}
      <Box>
        {/* Instrument Admin */}
        <FormControl fullWidth sx={{ mb: 2 }} error={false}>
          <InputLabel id="admin-select-label">Instrument Admin *</InputLabel>
          <Select
            labelId="admin-select-label"
            value={selectedAdmin}
            label="Instrument Admin *"
            onChange={(e) => {
              setSelectedAdmin(e.target.value);
              setInstrumentAllowances([]);
              setAllAllowed(true);
            }}
          >
            {allAdmins.map((admin) => (
              <MenuItem key={admin} value={admin}>
                {admin}
              </MenuItem>
            ))}
          </Select>
          <Box
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
              mt: 1,
              ml: 2,
            }}
          >
            Required
          </Box>
        </FormControl>

        {/* Show toggle only if instrument admin is set */}
        {selectedAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Switch
              checked={allAllowed}
              onChange={(e) => {
                setAllAllowed(e.target.checked);
                if (e.target.checked) {
                  setInstrumentAllowances([]);
                }
              }}
              color="success"
              sx={{
                mr: 2,
                '& .MuiSwitch-track': {
                  backgroundColor: allAllowed ? '#43d97a' : '#ff6b6b',
                },
              }}
            />
            <Typography>
              {allAllowed
                ? 'All instruments preapproved'
                : `${instrumentAllowances.length} instruments preapproved (max is ${MAX_INSTRUMENT_PREAPPROVED} per instance)`}
            </Typography>
          </Box>
        )}

        {/* Instrument Allowances selection/modification (last) - only show if not allAllowed */}
        {!allAllowed && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="instrument-allowances-label">Select Instrument Ids *</InputLabel>
            <Select
              labelId="instrument-allowances-label"
              label="Select Instrument Ids *"
              multiple
              value={instrumentAllowances.map((a) => a.id)}
              onChange={(e) => {
                const selectedIds =
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (!maxPreapproved(selectedIds.map((id) => ({ id })))) return;
                const newAllowances = selectedIds.map((id) => {
                  const existing = instrumentAllowances.find((a) => a.id === id);
                  return existing || { id };
                });
                setInstrumentAllowances(newAllowances);
              }}
              renderValue={(selected) =>
                Array.isArray(selected)
                  ? [...selected].sort((a, b) => a.localeCompare(b)).join(', ')
                  : ''
              }
              disabled={allAllowed}
              MenuProps={{
                disablePortal: true,
                disableEnforceFocus: true,
                disableAutoFocusItem: true,
              }}
            >
              <MenuItem
                disabled
                sx={{
                  color:
                    instrumentAllowances.length >= MAX_INSTRUMENT_PREAPPROVED
                      ? 'error.main'
                      : 'text.secondary',
                  fontWeight: instrumentAllowances.length >= MAX_INSTRUMENT_PREAPPROVED ? 700 : 400,
                  fontStyle: 'bold',
                  opacity: 1,
                  backgroundColor:
                    instrumentAllowances.length >= MAX_INSTRUMENT_PREAPPROVED
                      ? 'rgba(255, 0, 0, 0.08)'
                      : undefined,
                }}
                selected
              >
                {instrumentAllowances.length} instruments preapproved (max is{' '}
                {MAX_INSTRUMENT_PREAPPROVED} per instance)
              </MenuItem>
              {allInstruments.map((inst) => {
                const idx = instrumentAllowances.findIndex((a) => a.id === inst.instrumentId);
                const checked = idx !== -1;
                const maxSelected = !maxPreapproved(instrumentAllowances);
                const disabled = !checked && maxSelected;
                return (
                  <MenuItem
                    key={inst.instrumentId}
                    value={inst.instrumentId}
                    disabled={disabled}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      color: disabled ? 'text.disabled' : undefined,
                    }}
                  >
                    <Checkbox checked={checked} sx={{ mr: 1 }} disabled={disabled} />
                    <span
                      style={{
                        display: 'inline-block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'middle',
                        color: disabled ? '#bdbdbd' : undefined,
                      }}
                    >
                      {inst.instrumentId}
                    </span>
                  </MenuItem>
                );
              })}
            </Select>
            <Box
              sx={{
                color: instrumentAllowancesValid ? 'text.secondary' : 'error.main',
                fontSize: '0.8rem',
                mt: 1,
                ml: 2,
              }}
            >
              {instrumentAllowancesValid
                ? 'Required'
                : `Invalid: IDs must be unique (max ${MAX_INSTRUMENT_PREAPPROVED})`}
            </Box>
          </FormControl>
        )}
      </Box>
    </FormDialog>
  );
};
